import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { diff as jsonDiff } from 'jsondiffpatch';
import { isArray } from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';
import { useIdentify } from '@hungryroot/customer';
import { fullPageRedirect } from '@hungryroot/environment';
import { useExperiment } from '@hungryroot/experiments';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import { getApiV2CustomersOccasionEntityPreferencesPath } from '@hungryroot/rest/api/v2/customers/[id]/occasion_entity_preferences/get/get-api-v2-customers-customer_id-occasion_entity_preferences';
import { getApiV2OccasionPreferenceOptionsPath } from '@hungryroot/rest/api/v2/occasion_preference_options/get/get-api-v2-occasion_preference_options';
import { tags } from '@hungryroot/tags';
import {
  getQuizToast,
  type QuizToastObj,
} from './components/QuizToast/quiz-toast';
import { serverPersist } from './data/server-persist';
import {
  getQuizLocalStorageExpiry,
  LOCAL_STORAGE_KEY,
  QUIZ_EXPIRY_LOCAL_STORAGE_KEY,
  QUIZ_RESET_TTL,
} from './defs/localStorage';
import {
  type ProteinTagValue,
  type NonNullableQuiz,
  defaultQuiz,
} from './defs/quiz';
import { type RouteGroup } from './defs/route-groups';
import {
  ROUTE_FOOD_PREFERENCES_INTRO,
  ROUTE_QUIZ_HELLO,
  type AllQuizRoutes,
} from './defs/routes';
import { useQuizDataNormalizer } from './hooks/useQuizDataNormalizer';
import { useQuizLoader } from './hooks/useQuizLoader';
import { useQuizMutations } from './hooks/useQuizMutations';
import {
  type NavigationNode,
  useQuizNavigation,
} from './hooks/useQuizNavigation';
import { setSavedNavState } from './navigation/navigation';
import {
  getQuizProgressSections,
  quizProgressAbout as defaultQuizProgressAbout,
  quizProgressRoutine as defaultQuizProgressRoutine,
  quizProgressTastes,
} from './navigation/progress-sections';

type Nullable<T> = { [K in keyof T]: T[K] | null };

export { type NonNullableQuiz };

export type QuizTagKeys = keyof Pick<
  Quiz,
  | 'dislike'
  | 'breakfast'
  | 'snacks'
  | 'sweets'
  | 'macroNutrient'
  | 'foodQualities'
  | 'foodFormats'
  | 'readyMade'
  | 'budget'
  | 'lunch'
  | 'dinner'
>;

export type QuizBinaryTagKeys = keyof Pick<Quiz, 'cuisines' | 'flavors'>;

export type QuizTernaryTagKeys = keyof Pick<
  Quiz,
  'meats' | 'seafood' | 'plants'
>;

export type QuizFrequencyKeys = keyof Pick<
  Quiz,
  | 'breakfastFrequency'
  | 'lunchFrequency'
  | 'dinnerFrequency'
  | 'snacksFrequency'
  | 'sweetsFrequency'
>;

export type Quiz = Nullable<NonNullableQuiz>;

type FlatQuizTypes = {
  [K in keyof NonNullableQuiz]: NonNullableQuiz[K] extends (
    | number
    | string
    | ProteinTagValue
  )[]
    ? NonNullableQuiz[K][number]
    : NonNullableQuiz[K];
};

export type QuizTypes = FlatQuizTypes[keyof FlatQuizTypes];

const QUIZ_KEY = ['quiz'];

interface UseQuizResultNullQuiz {
  quiz: null;
  redirectPath?: undefined;
  quizToast: QuizToastObj | undefined;
}

interface UseQuizResultRedirect {
  quiz: null;
  redirectPath: AllQuizRoutes;
  quizToast: QuizToastObj | undefined;
}

export interface UseQuizResultWithQuiz<T> {
  quiz: Quiz;
  onSubmit(quiz: Quiz, continueOnSubmit?: boolean): void;
  navigationState: NavigationNode;
  onQuestionAnswer<T extends keyof Quiz>(
    quiz: Quiz,
    question: T,
    value: Quiz[T]
  ): void;
  onSaveAndExit(quiz: Quiz): void;
  onQuizInit(): void;
  quizToast: QuizToastObj | undefined;
  screens: ReactNode[];
  sections: RouteGroup<T>[];
}

type UseQuizResult<T> =
  | UseQuizResultNullQuiz
  | UseQuizResultWithQuiz<T>
  | UseQuizResultRedirect;

export function loadQuizFromLocalStorage(): Quiz | null {
  const value = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  if (value === null) {
    return null;
  }

  return JSON.parse(value);
}

function getCustomerId(
  customerQuery: ReturnType<typeof useIdentify>
): number | null {
  const { customerData } = customerQuery;
  if (customerData === undefined) {
    return null;
  }

  return customerData.id;
}

/** Special case where we want to remove the low sodium hard constraint if the soft constraint is removed */
const handleLowSodiumTags = <K extends keyof Quiz>(
  q: Quiz,
  question: K,
  value: NonNullableQuiz[K]
) => {
  if (question === 'macroNutrient' && isArray(value)) {
    if (!(value as number[]).includes(tags.LowSodium)) {
      return value.filter((tag) => tag !== tags.OnlyLowSodium);
    }
  }

  return value;
};

interface Props {
  onQuestionSubmit?(params: {
    nextNavigationState: NavigationNode;
    quiz: Quiz;
  }): void;
  onQuizComplete: () => void;
}

export function useQuiz({ onQuizComplete, onQuestionSubmit }: Props) {
  const customerSelf = useIdentify();
  const query = useQuizLoader();
  const history = useHistory();
  const { pathname, search: searchParams } = useLocation();
  const quizMutations = useQuizMutations();
  const persistQuiz = usePersistQuizMutation();
  const [persistedQuiz, setPersistedQuiz] = useState(query.data);
  const [inMemoryQuiz, setInMemoryQuiz] = useState<Quiz | undefined>();
  const [quizToast, setQuizToast] = useState<QuizToastObj | undefined>();

  const dietDrivenPreferencesVariant = useExperiment(175);

  if (persistedQuiz !== query.data) {
    setPersistedQuiz(query.data);
    setInMemoryQuiz(query.data === null ? defaultQuiz : query.data);
  }

  const quiz = inMemoryQuiz;
  const setQuiz = setInMemoryQuiz;

  const navigationState = useQuizNavigation({
    loc: pathname,
    searchParams,
    query,
    quiz,
  });

  const quizDataNormalizer = useQuizDataNormalizer(quiz);

  if (
    quiz === undefined ||
    navigationState === undefined ||
    quizMutations === undefined ||
    dietDrivenPreferencesVariant === undefined
  ) {
    return {
      quiz: null,
      quizToast: undefined,
    };
  }

  const quizProgressRoutine =
    dietDrivenPreferencesVariant > 0
      ? [ROUTE_FOOD_PREFERENCES_INTRO, ...defaultQuizProgressRoutine]
      : defaultQuizProgressRoutine;

  const progressSections = getQuizProgressSections<AllQuizRoutes>(
    defaultQuizProgressAbout,
    quizProgressTastes,
    quizProgressRoutine as AllQuizRoutes[]
  );

  const { redirect: redirectPath } = navigationState;

  if (redirectPath !== undefined) {
    return {
      quiz: null,
      redirectPath,
      quizToast: undefined,
    };
  }

  const { navigation, routes: quizRoutes } = navigationState;
  const { path: currentQuizPath } = navigation;
  setSavedNavState(currentQuizPath);

  const customerId = getCustomerId(customerSelf);

  const onQuestionAnswer = <K extends keyof Quiz>(
    q: Quiz,
    question: K,
    value: NonNullableQuiz[K]
  ) => {
    const computedValue = handleLowSodiumTags(
      q,
      question,
      value
    ) as typeof value;
    const nextQuiz = quizMutations.onQuestionAnswer(q, question, computedValue);
    const quizToast = getQuizToast(nextQuiz, q, {
      goals: true,
    });
    setQuizToast(quizToast);
    setQuiz(nextQuiz);

    return nextQuiz;
  };

  const result: UseQuizResult<Exclude<typeof quizRoutes, undefined>[number]> = {
    sections: progressSections,
    screens: [],
    quiz,
    navigationState: navigation,
    quizToast,
    onQuestionAnswer,
    onSubmit(
      nextQuiz: Quiz,
      continueOnSubmit: boolean = true,
      onSubmitAnalytics?: () => void
    ) {
      const normalizedNextQuiz = quizDataNormalizer.normalizeData(nextQuiz);

      const mutationParams: QuizMutationParams =
        customerId === null
          ? {
              quiz: normalizedNextQuiz,
              customerId,
            }
          : {
              quiz: normalizedNextQuiz,
              customerId,
              persistedQuiz: persistedQuiz as Quiz,
            };

      persistQuiz.mutate(mutationParams, {
        onSuccess() {
          const nextNavigationState = navigation;
          const { next } = nextNavigationState;

          onQuestionSubmit?.({
            nextNavigationState,
            quiz: normalizedNextQuiz,
          });

          if (next === null) {
            onQuizComplete();
            return;
          }

          if (continueOnSubmit) {
            history.push(next);
          }
        },
      });

      onSubmitAnalytics?.();

      const quizToast = getQuizToast(nextQuiz, quiz);
      setQuizToast(quizToast);
    },
    onSaveAndExit(nextQuiz: Quiz) {
      const nextNavigationState = navigation;
      const { next } = nextNavigationState;
      if (next === ROUTE_QUIZ_HELLO) {
        return fullPageRedirect('/');
      }

      const normalizedNextQuiz = quizDataNormalizer.normalizeData(nextQuiz);

      const mutationParams: QuizMutationParams =
        customerId === null
          ? {
              quiz: normalizedNextQuiz,
              customerId,
            }
          : {
              quiz: normalizedNextQuiz,
              customerId,
              persistedQuiz: persistedQuiz as Quiz,
            };
      persistQuiz.mutate(mutationParams, {
        onSuccess() {
          fullPageRedirect('/');
        },
      });
    },
    onQuizInit() {
      // Note, we do not need to normalize quiz data here for meal type questions because this is only used to initialize with default quiz data.
      const nextQuiz = { ...defaultQuiz };

      const mutationParams: QuizMutationParams =
        customerId === null
          ? {
              quiz: nextQuiz,
              customerId,
            }
          : {
              quiz: nextQuiz,
              customerId,
              persistedQuiz: persistedQuiz as Quiz,
            };

      persistQuiz.mutate(mutationParams, {
        onSuccess() {
          const nextNavigationState = navigation;
          const { next } = nextNavigationState;
          if (next === null) {
            return;
          }
          history.push(next);
        },
      });
    },
  };

  return result;
}

/**
 * For EXP-158, 815 and 811 need to have the same values (grass fed and pasture raised)
 * However, only grass fed is shown in the quiz.
 * This means:
 *  - if grass fed is selected, and pasture raised is not, add pasture raised
 *  - if grass fed is not selected, and pasture raised is, remove pasture raised
 */
function handleFoodQualitiesForQuiz(quiz: Quiz) {
  // If we have a loaded quiz that does not have foodQualities, return it as is
  if (quiz.foodQualities === undefined) return quiz;

  let updatedFoodQualities: number[] | null = quiz.foodQualities;
  if (
    quiz.foodQualities !== null &&
    quiz.foodQualities?.includes(815) &&
    !quiz.foodQualities?.includes(811)
  ) {
    // add pasture raised if grass fed meat is selected
    updatedFoodQualities = [...quiz.foodQualities, 811];
  }
  if (
    quiz.foodQualities !== null &&
    quiz.foodQualities !== undefined &&
    !quiz.foodQualities?.includes(815)
  ) {
    // remove pasture raised if grass fed meat is not selected
    updatedFoodQualities =
      quiz.foodQualities?.filter((id) => id !== 811) ?? null;
  }

  return {
    ...quiz,
    foodQualities: updatedFoodQualities,
  } as Quiz;
}

type QuizMutationParams =
  | {
      quiz: Quiz;
      customerId: null;
    }
  | {
      quiz: Quiz;
      customerId: number;
      persistedQuiz: Quiz;
    };

function usePersistQuizMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (params: QuizMutationParams) => {
      const { quiz, customerId } = params;

      if (customerId === null) {
        // customer is not logged in, persist changes locally
        localStorage.setItem(
          LOCAL_STORAGE_KEY,
          JSON.stringify(handleFoodQualitiesForQuiz(quiz))
        );
        localStorage.setItem(
          QUIZ_EXPIRY_LOCAL_STORAGE_KEY,
          JSON.stringify(getQuizLocalStorageExpiry(QUIZ_RESET_TTL))
        );
        return {
          diff: true,
        };
      }

      const { persistedQuiz } = params;
      const diff = jsonDiff(quiz, persistedQuiz);

      if (diff === undefined) {
        return {
          diff: false,
        };
      }

      await serverPersist(customerId, handleFoodQualitiesForQuiz(quiz), {
        originalQuiz: handleFoodQualitiesForQuiz(persistedQuiz),
      });
      return {
        diff: true,
      };
    },
    onSuccess(data: { diff: boolean }, params: QuizMutationParams) {
      const { diff } = data;
      const { customerId } = params;

      if (customerId === null) {
        // only invalidate local quiz if user is anonymous
        client.invalidateQueries({ queryKey: [QUIZ_KEY, 'local'] });
        return;
      }

      if (diff === false) {
        return;
      }
      client.invalidateQueries({ queryKey: [QUIZ_KEY, 'api'] });

      // Query invalidation for exp 175
      // Invalidate customer dietary restrictions query. Result passed as a param in occasion preference option query.
      client.invalidateQueries({ queryKey: ['customer-dietary-restrictions'] });
      // Invalidate occasion preference option query. Results used to populate data on meal type pages
      client.invalidateQueries({
        queryKey: [getApiV2OccasionPreferenceOptionsPath()],
      });
      // Invalidate customer occasion preferences. Results used to populate selected items on meal type pages
      client.invalidateQueries({
        queryKey: [getApiV2CustomersOccasionEntityPreferencesPath()],
      });
    },
    onError(error: unknown, variables: QuizMutationParams) {
      logUnknownErrorResponse('Failed to persist quiz data to server', {
        key: 'quiz.serverPersist',
        error: error instanceof Error ? error.message : String(error),
        customerId: variables.customerId,
      });
    },
  });
}
